/**
 * D-035: kesim önerisi. Kaynak metinden (bulgu kaydı, ADDITIONAL satırları) kesim noktalarını ÖNERİR; karar insanındır
 * ("sistem ham cümleyi taşır, yorum insan onaylıdır"). Öneri kaydedilmez: pencere her açılışta, sunucu her onayda yeniden hesaplar.
 *
 * Kurallar:
 * 1. Mesafe yalnız KAPANIŞ disposition alıntısından okunur (`closing disposition: "…" (p.36)`; iki satırlı `"…" + "…" (s.33)`).
 *    Biçimler: "a-bM", "a–b m", "a to bM", "pM", "p m location". "splice weld at …", "at"sız "splice weld 1m" (D-054; yalnız
 *    splice weld için, çıplak "weld" yine "at" ister), "weld at …", mm ve WT değerleri kesim değildir. Boru referansının ara kaynak
 *    önerisi yalnız "splice weld at …" yazımından gelir.
 * 2. Okunan mesafeler korozyon haritasının "Below Tolerance" girdileriyle karşılaştırılır (±500 mm hoşgörü). Uyuşmazsa öneri
 *    0 kesimdir ve pencere "Disposition and corrosion map disagree" der. "Above Tolerance" girdileri yalnız gösterilir.
 * 3. Kapanış alıntısında mesafe yoksa bulgu, önerilen iş ve komponentin reddedilmiş/çelişkili ADDITIONAL satırları sırayla
 *    taranır ("from finding text, confirm").
 * 4. Aralık → a−300 ve b+300 mm; nokta → p−500 ve p+500 mm; çakışan aralıklar birleşir. Box flanş kaynağına (yoksa box ucuna)
 *    ulaşan kesim Box to pipe, pin flanş kaynağına (yoksa boya) ulaşan Pin to pipe satırı olur.
 * 5. ST-04: kalem adındaki uçta (pin/box) flanş kaynağında tek kesim. ST-06: "Girth Weld N" konumsal kuralla (1 = box,
 *    son = pin, arada ara kaynaklar); yoksa kalem adındaki uç. ST-05: var olan kaynakta tek "weld repair" satırı, kesim yok.
 * 6. D-054 boydan boya değişim (ST-03): boru boydan boya değişiyorsa (`tamBoyDegisimMi`) öneri iki kesimdir: box flanş kaynağı
 *    (Box to pipe) ve pin flanş kaynağı (Pin to pipe); aralık kesimi kurulmaz. Sıra: (a) kapanış alıntısı boydan boya diyorsa
 *    boydan boya; (b) değilse kapanış mesafe veriyorsa kural 1–2; (c) değilse önerilen iş (bulgusuz eski işte iş talimatı) boydan
 *    boya diyorsa boydan boya; (d) değilse kural 3. Bulgu metni ve ADDITIONAL satırları bu kural için taranmaz. Bulgu metnindeki
 *    kusur yerleri yine okunur (şema bantları ve pencerede "Not cut points"), kesim üretmez; tetikleyen cümle kaynak bloğuna girer.
 *    İşin bir boru bulgusu boydan boya ise işin bütün boru bulgularının aralık kesimleri düşer (boru bütünüyle değişir); flanş,
 *    flanş kurtarma ve kaynak tamiri adayları kalır ve bugünkü gibi tekilleşir.
 * 7. D-055 yeni borunun ek kaynağı: boydan boya öneride (kural 6) yeni boru en az iki boydan birleşir; bütün kesim ve kaynak tamiri
 *    satırlarından sonra tek bir ek kaynağı satırı önerilir (tur "ek_kaynagi", Pipe to pipe butt, mesafesiz; kesim değil, bir kaynak
 *    ve bir fit-up formu). Boydan boya olmayan öneride ek kaynağı satırı yoktur; fazladan boylar için kişi elle ekler.
 *
 * Desenlerde geriye bakış ("(?<…)") yok: eski Safari desen kurulurken hata atar. Önceki karakter tüketilerek denetlenir.
 */
import { BORU_BAGLANTI, BOX_BAGLANTI, KESIM_PAYI_MM, NOKTA_PAYI_MM, PIN_BAGLANTI, VARSAYILAN_BOY_MM, kesimKonumu, kesimTuru, } from "./kesim-plani.js";
/* ------------------------------------------------------------ metin okuma ------------------------------------------------------------ */
const ALINTI = /["“]([^"”]*)["”]/g;
const KAPANIS_BASI = /closing disposition/gi;
const KAPANIS = /^closing disposition[^"“]{0,60}((?:["“][^"”]*["”]\s*(?:\+\s*)?)+)/i;
/** "(p.36)", "(s.33)", "(Wall thickness is below tolerance, s.33-34)" */
const SAYFA = /^\s*\((?:[^()]*?[\s,])?(?:p|s)\.\s*(\d+)(?:\s*[-–]\s*\d+)?\)/i;
const HARITA_SAYFA = /^corrosion map\s*\((?:p|s)\.\s*(\d+)\)/i;
const SPLICE = /splice weld (?:is located )?at (\d+(?:\.\d+)?)\s*m(?![a-z])/gi;
/** "splice weld at 12m" ve "at"sız "splice weld 1m" (D-054): kusur yeri değil, "Not cut points" satırında birebir gösterilir. */
const SPLICE_IFADESI = /splice\s+weld\s+(?:is\s+located\s+)?(?:at\s+)?\d+(?:\.\d+)?\s*m(?![a-z])/gi;
/**
 * D-054: boydan boya boru değişimi (Bekir 02.10.2026, "Hepsi bu şekilde sayılsın."): "…cutting the Pin and box connectors off and
 * replacing the full length of pipe", "replace full length of main tube pipe and weld connectors to a new pipe", "Reject whole
 * length of pipe", "cutting and replacing the whole length of pipe", "length of pipe to be cut from the two end connectors". Desen
 * fiile (replace / reject) ya da bağlantı sözüne bağlıdır: "Full length of Main tube pipe to be visually and dimensionally
 * inspected" (doğrulama kapsamı), "noted across full length of pipe" (bulgu) tutmaz.
 */
const TAM_BOY = /\b(?:replac(?:e|ed|es|ing)|reject(?:ed|s)?)\s+(?:the\s+)?(?:full|whole)\s+length\s+of\s+(?:the\s+)?(?:main\s+tube\s+)?pipe\b(?!\s+to\s+be\s+(?:visually|dimensionally|inspected))|\blength\s+of\s+(?:the\s+)?pipe\s+to\s+be\s+cut\s+from\s+(?:the\s+)?(?:two|both)\s+end\s+connectors\b/i;
/** D-054: metin borunun boydan boya değiştirileceğini söylüyor mu (büyük/küçük harf ve satır sonu fark etmez). */
export function tamBoyDegisimMi(metin) {
    return Boolean(metin) && TAM_BOY.test(metin ?? "");
}
/** Kapanış disposition alıntıları (tırnaksız) ve ilk sayfa no; "closing disposition" yoksa null. */
export function kapanisCumlesi(metin) {
    if (!metin)
        return null;
    for (const bas of metin.matchAll(KAPANIS_BASI)) {
        const kalan = metin.slice(bas.index);
        const m = KAPANIS.exec(kalan);
        if (!m)
            continue;
        const alintilar = [...(m[1] ?? "").matchAll(ALINTI)].map((a) => (a[1] ?? "").trim()).filter(Boolean);
        if (!alintilar.length)
            continue;
        const s = SAYFA.exec(kalan.slice(m[0].length));
        return { alintilar, sayfa: s ? Number(s[1]) : null };
    }
    return null;
}
/** "corrosion map" ile "closing disposition" arasındaki tırnaklı girdiler. */
export function haritaGirdileri(metin) {
    if (!metin)
        return [];
    const i = metin.search(/corrosion map/i);
    if (i < 0)
        return [];
    let parca = metin.slice(i);
    const j = parca.search(/closing disposition/i);
    if (j >= 0)
        parca = parca.slice(0, j);
    const sm = HARITA_SAYFA.exec(parca);
    const sayfa = sm ? Number(sm[1]) : null;
    return [...parca.matchAll(ALINTI)]
        .flatMap((a) => (a[1] ?? "").split(" · "))
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => {
        const asagi = /below tolerance/i.test(s);
        const ilk = belirtecler(s, null).sort((a, b) => a.yer - b.yer)[0];
        return { metin: s, asagi, ust: !asagi && /above/i.test(s), konum: ilk ? ilk.konum : null, sayfa };
    });
}
/** Metnin mesafe belirteçleri (yer = metindeki konum). Sıra: dışlananlar, "box/pin end for", aralıklar, noktalar. */
function belirtecler(metin, boy) {
    let s = metin;
    const bosalt = (bas, uzunluk) => {
        s = s.slice(0, bas) + " ".repeat(uzunluk) + s.slice(bas + uzunluk);
    };
    const mm = (x) => Math.round(Number(x) * 1000);
    const out = [];
    // a. dışlananlar: "splice weld at 12m", "weld at 5m", "weld is located at 12m"; D-054: "at"sız yalnız "splice weld 1m"
    for (const m of [...s.matchAll(/(?:splice\s+weld\s+(?:is\s+located\s+)?(?:at\s+)?|weld\s+(?:is\s+located\s+)?at\s+)\d+(?:\.\d+)?\s*m(?![a-z])/gi)])
        bosalt(m.index, m[0].length);
    // b. "box end for 3m and 1.75m" → [0, 3000]; "pin end for 2m" → [L − 2000, L]
    for (const m of [...s.matchAll(/(box|pin)\s+end\s+for\s+(\d+(?:\.\d+)?)\s*m(?![a-z])((?:\s*(?:and|&|,)\s*\d+(?:\.\d+)?\s*m(?![a-z]))*)/gi)]) {
        const degerler = [mm(m[2]), ...[...(m[3] ?? "").matchAll(/\d+(?:\.\d+)?/g)].map((x) => mm(x[0]))];
        const en = Math.max(...degerler);
        const L = boy ?? VARSAYILAN_BOY_MM;
        out.push({ konum: (m[1] ?? "").toLowerCase() === "box" ? { bas: 0, son: en } : { bas: Math.max(0, L - en), son: L }, yer: m.index });
        bosalt(m.index, m[0].length);
    }
    // c. aralıklar: "1-3M", "1–3 m", "0.5M to 3.5M", "1m to 3m"
    for (const m of [...s.matchAll(/(^|[^\w.])(\d+(?:\.\d+)?)\s*(?:m\s*)?(?:-|–|to)\s*(\d+(?:\.\d+)?)\s*m(?![a-z])/gi)]) {
        const a = mm(m[2]);
        const b = mm(m[3]);
        const bas = m.index + (m[1] ?? "").length;
        out.push({ konum: { bas: Math.min(a, b), son: Math.max(a, b) }, yer: bas });
        bosalt(bas, m[0].length - (m[1] ?? "").length);
    }
    // d. noktalar: "7.5M", "17m", "21 m location"
    for (const m of [...s.matchAll(/(^|[^\w.])(\d+(?:\.\d+)?)\s*m(?![a-z])/gi)]) {
        const p = mm(m[2]);
        out.push({ konum: { bas: p, son: p }, yer: m.index + (m[1] ?? "").length });
    }
    return out;
}
const tekil = (k) => {
    const g = new Map();
    for (const x of k)
        g.set(`${x.bas}-${x.son}`, x);
    return [...g.values()].sort((a, b) => a.bas - b.bas || a.son - b.son);
};
/** Metindeki mesafeler (box ucundan mm; tekil, bas sırasıyla). Birim yalnız tek başına "m": "2.5mm", "19.3mm WT" tutmaz. */
export function metreKonumlari(metin, secenek) {
    return tekil(belirtecler(metin, secenek?.boy).map((b) => b.konum));
}
/** İki konum ±500 mm hoşgörüyle örtüşüyor mu. */
const ortusur = (a, b) => a.bas <= b.son + NOKTA_PAYI_MM && b.bas <= a.son + NOKTA_PAYI_MM;
/** Kapanış mesafeleri haritayla uyuşuyor mu: her biri öbür listede bir karşılık bulmalı. */
const uyusur = (k, h) => k.every((a) => h.some((b) => ortusur(a, b))) && h.every((b) => k.some((a) => ortusur(a, b)));
/** Metindeki ilk "pin" ya da "box" kelimesi. */
function tarafBul(metin) {
    const m = /\b(pin|box)\b/i.exec(metin ?? "");
    return m ? (m[1] ?? "").toLowerCase() : null;
}
const spliceIfadeleri = (metinler) => {
    const out = [];
    for (const t of metinler)
        for (const m of (t ?? "").matchAll(SPLICE_IFADESI))
            if (!out.includes(m[0]))
                out.push(m[0]);
    return out;
};
/* ------------------------------------------------------------ referans ------------------------------------------------------------ */
/** Boru referansı önerisi: başka işin onaylı planı öncelikli; yoksa ara kaynaklar "splice weld at …" metninden, boy 22.860. */
export function referansOnerisi(g) {
    if (g.referans) {
        const r = g.referans;
        return {
            referans: { box: r.box, ara: [...new Set(r.ara)].sort((a, b) => a - b), pin: r.pin, boy: r.boy },
            kanit: ["box", "ara", "pin", "boy"].map((alan) => ({ alan, durum: "baska_plan", metin: null })),
        };
    }
    const metinler = [
        ...g.bulgular.flatMap((b) => [b.kaynakNot, b.bulgu, b.onerilenIs]),
        ...g.ekSatirlar.map((e) => e.kanit),
        ...g.referansMetinleri,
        g.dispoNot,
    ];
    const ifadeler = [];
    const ara = [];
    for (const t of metinler) {
        for (const m of (t ?? "").matchAll(SPLICE)) {
            ifadeler.push(m[0]);
            ara.push(Math.round(Number(m[1]) * 1000));
        }
    }
    return {
        referans: { box: null, ara: [...new Set(ara)].sort((a, b) => a - b), pin: null, boy: VARSAYILAN_BOY_MM },
        kanit: [
            { alan: "box", durum: "yok", metin: null },
            ara.length ? { alan: "ara", durum: "metinden", metin: ifadeler[0] ?? null } : { alan: "ara", durum: "yok", metin: null },
            { alan: "pin", durum: "yok", metin: null },
            { alan: "boy", durum: "varsayilan", metin: null },
        ],
    };
}
/** Bir bulgunun (ya da bulgusuz eski işte dispo cümlesinin) okunması. */
function bulguyuOku(b, g, ref, dispo) {
    const tur = kesimTuru(b.st ?? g.st);
    const kap = kapanisCumlesi(b.kaynakNot);
    const harita = haritaGirdileri(b.kaynakNot);
    const kaynaklar = [];
    const cumle = (t, metin, sayfa = null) => {
        if (metin && metin.trim())
            kaynaklar.push({ no: dispo ? null : b.no, tur: t, metin: metin.trim(), sayfa });
    };
    if (dispo)
        cumle("dispo", b.kaynakNot);
    else if (kap)
        for (const a of kap.alintilar)
            cumle("kapanis", a, kap.sayfa);
    for (const h of harita)
        if (h.asagi)
            cumle("harita", h.metin, h.sayfa);
    if (!kap && !dispo) {
        cumle("bulgu", b.bulgu);
        cumle("onerilen", b.onerilenIs);
    }
    const sonuc = {
        no: dispo ? null : b.no,
        tur,
        karar: "bulunamadi",
        tamBoyKaynagi: null,
        konumlar: [],
        haritaKonumlari: tekil(harita.filter((h) => h.asagi && h.konum).map((h) => h.konum)),
        ustTolerans: tekil(harita.filter((h) => h.ust && h.konum).map((h) => h.konum)),
        yoksayilanKaynak: spliceIfadeleri([b.kaynakNot, b.bulgu, b.onerilenIs]),
        celiskili: false,
        taraf: null,
        girthNo: null,
    };
    const adaylar = [];
    const flansAdayi = (taraf, t = "kesim") => taraf === "box" ? { tur: t, baglanti: BOX_BAGLANTI, mesafeMm: ref.box } : { tur: t, baglanti: PIN_BAGLANTI, mesafeMm: ref.pin };
    const kapanisMetni = kap ? kap.alintilar.join(" ") : "";
    if (tur === "boru") {
        const K = kap ? metreKonumlari(kapanisMetni, { boy: ref.boy }) : [];
        // D-054 sırası: kapanış boydan boya → kapanışın mesafeleri → önerilen iş (bulgusuz eski işte iş talimatı) boydan boya → yedek.
        const tamBoy = kap && tamBoyDegisimMi(kapanisMetni) ? "kapanis"
            : K.length ? null
                : dispo ? (tamBoyDegisimMi(b.kaynakNot) ? "dispo" : null)
                    : tamBoyDegisimMi(b.onerilenIs) ? "onerilen" : null;
        if (tamBoy) {
            sonuc.karar = "tam_boy";
            sonuc.tamBoyKaynagi = tamBoy;
            // Bulgu metnindeki kusur yerleri yalnız şema bantları ve "Not cut points" içindir; kesim üretmez.
            sonuc.konumlar = b.bulgu ? metreKonumlari(b.bulgu, { boy: ref.boy }) : [];
            // Kapanış alıntısı varken bulgu ve önerilen iş kaynak bloğuna bugün yazılmaz: tetikleyen cümle ve kusur yerlerinin metni eklenir.
            if (kap && !dispo) {
                if (sonuc.konumlar.length)
                    cumle("bulgu", b.bulgu);
                if (tamBoy === "onerilen")
                    cumle("onerilen", b.onerilenIs);
            }
            adaylar.push(flansAdayi("box"), flansAdayi("pin"));
        }
        else if (K.length) {
            sonuc.konumlar = K;
            const H = sonuc.haritaKonumlari;
            sonuc.karar = H.length === 0 ? "harita_yok" : uyusur(K, H) ? "uyumlu" : "celiski";
        }
        else {
            // Yedek: bulgu → önerilen iş → reddedilmiş/çelişkili ADDITIONAL satırları (bulgusuz eski işte dispo cümlesi).
            const kaynaklarSirasi = dispo
                ? [{ tur: "dispo", metin: b.kaynakNot }]
                : [
                    { tur: "bulgu", metin: b.bulgu },
                    { tur: "onerilen", metin: b.onerilenIs },
                ];
            for (const e of g.ekSatirlar)
                if (/reject|conflict/i.test(`${e.sonuc ?? ""} ${e.kanit ?? ""}`))
                    kaynaklarSirasi.push({ tur: "ek", metin: e.kanit, ek: e });
            for (const k of kaynaklarSirasi) {
                if (!k.metin)
                    continue;
                if (k.ek && /closing disposition/i.test(k.metin)) {
                    const ekKap = kapanisCumlesi(k.metin);
                    const K2 = ekKap ? metreKonumlari(ekKap.alintilar.join(" "), { boy: ref.boy }) : [];
                    if (!K2.length)
                        continue;
                    const ekHarita = haritaGirdileri(k.metin);
                    const H2 = tekil(ekHarita.filter((h) => h.asagi && h.konum).map((h) => h.konum));
                    sonuc.konumlar = K2;
                    sonuc.haritaKonumlari = H2;
                    sonuc.ustTolerans = tekil(ekHarita.filter((h) => h.ust && h.konum).map((h) => h.konum));
                    sonuc.karar = H2.length === 0 || uyusur(K2, H2) ? "metinden" : "celiski";
                }
                else {
                    const K3 = metreKonumlari(k.metin, { boy: ref.boy });
                    if (!K3.length)
                        continue;
                    sonuc.konumlar = K3;
                    sonuc.karar = "metinden";
                }
                if (k.ek) {
                    sonuc.celiskili = /conflict/i.test(`${k.ek.sonuc ?? ""} ${k.ek.kanit ?? ""}`);
                    sonuc.yoksayilanKaynak = [...new Set([...sonuc.yoksayilanKaynak, ...spliceIfadeleri([k.metin])])];
                    cumle("ek", k.metin, kapanisCumlesi(k.metin)?.sayfa ?? null);
                }
                break;
            }
        }
        // Kesim adayları ayrıca (bütün bulgular birlikte) kesimOnerisi'nde aralıklardan kurulur; boydan boyada iki flanş adayı yukarıda.
    }
    else if (tur === "flans") {
        const taraf = tarafBul(b.kalem) ?? tarafBul(b.bulgu) ?? tarafBul(kapanisMetni);
        sonuc.taraf = taraf;
        if (taraf) {
            sonuc.karar = "kalem_adindan";
            adaylar.push(flansAdayi(taraf));
        }
    }
    else if (tur === "flans_kurtarma") {
        const gm = /girth\s+weld\s*(\d+)/i.exec(b.kalem);
        const n = gm ? Number(gm[1]) : 0;
        let aday = null;
        if (n >= 1) {
            sonuc.girthNo = n;
            if (n === 1) {
                sonuc.taraf = "box";
                aday = flansAdayi("box");
            }
            else if (n >= 2 + ref.ara.length) {
                sonuc.taraf = "pin";
                aday = flansAdayi("pin");
            }
            else
                aday = { tur: "kesim", baglanti: BORU_BAGLANTI, mesafeMm: ref.ara[n - 2] ?? null };
        }
        else {
            const taraf = tarafBul(b.kalem) ?? tarafBul(b.bulgu) ?? tarafBul(kapanisMetni);
            sonuc.taraf = taraf;
            if (taraf)
                aday = flansAdayi(taraf);
        }
        if (aday) {
            adaylar.push(aday);
            sonuc.karar = kap && !/solution/i.test(kapanisMetni) ? "cozum_yok" : "kalem_adindan";
        }
    }
    else {
        // ST-05: var olan flanş kaynağında tamir; kesim yok, bir kaynak.
        const taraf = tarafBul(b.kalem) === "box" ? "box" : "pin";
        sonuc.taraf = taraf;
        sonuc.karar = "kaynak_tamiri";
        adaylar.push(flansAdayi(taraf, "kaynak_tamiri"));
    }
    return { sonuc, kaynaklar, adaylar };
}
/** İşin kesim önerisi (bütün bulguları birlikte; grup = tek iş = tek plan). */
export function kesimOnerisi(g) {
    const { referans, kanit } = referansOnerisi(g);
    const boxPos = referans.box ?? 0;
    const pinPos = referans.pin ?? referans.boy ?? VARSAYILAN_BOY_MM;
    const okunan = g.bulgular.length
        ? g.bulgular.map((b) => bulguyuOku(b, g, referans, false))
        : g.dispoNot
            ? [bulguyuOku({ no: 0, kalem: "", st: g.st, kaynakNot: g.dispoNot, bulgu: null, onerilenIs: null }, g, referans, true)]
            : [];
    const bulgular = okunan.map((o) => o.sonuc);
    // Kaynak bloğu: aynı cümle iki kez yazılmaz (grupta ortak ADDITIONAL satırı).
    const kaynaklar = [];
    for (const k of okunan.flatMap((o) => o.kaynaklar))
        if (!kaynaklar.some((x) => x.tur === k.tur && x.metin === k.metin))
            kaynaklar.push(k);
    // Boru kesimleri: kabul edilen konumlar paylanır, çakışanlar birleşir, her aralık iki kesim verir.
    // D-054: bir boru bulgusu boydan boya ise boru bütünüyle değişir; hiçbir boru bulgusu aralık kesimi vermez.
    const tamBoy = bulgular.some((b) => b.tur === "boru" && b.karar === "tam_boy");
    const aralik = bulgular
        .filter((b) => !tamBoy && b.tur === "boru" && (b.karar === "uyumlu" || b.karar === "harita_yok" || b.karar === "metinden"))
        .flatMap((b) => b.konumlar)
        .map((k) => (k.bas === k.son ? { bas: k.bas - NOKTA_PAYI_MM, son: k.son + NOKTA_PAYI_MM } : { bas: k.bas - KESIM_PAYI_MM, son: k.son + KESIM_PAYI_MM }))
        .sort((a, b) => a.bas - b.bas);
    const birlesik = [];
    for (const a of aralik) {
        const son = birlesik[birlesik.length - 1];
        if (son && a.bas <= son.son)
            son.son = Math.max(son.son, a.son);
        else
            birlesik.push({ ...a });
    }
    const noktadan = (x) => x <= boxPos
        ? { tur: "kesim", baglanti: BOX_BAGLANTI, mesafeMm: referans.box }
        : x >= pinPos
            ? { tur: "kesim", baglanti: PIN_BAGLANTI, mesafeMm: referans.pin }
            : { tur: "kesim", baglanti: BORU_BAGLANTI, mesafeMm: x };
    const adaylar = [...birlesik.flatMap((k) => [noktadan(k.bas), noktadan(k.son)]), ...okunan.flatMap((o) => o.adaylar)];
    // Tekilleştir (Box, Pin, Pipe to pipe mm, kaynak tamiri + bağlantı) ve konuma göre sırala.
    const anahtar = (a) => `${a.tur}|${a.baglanti}|${a.baglanti === BORU_BAGLANTI ? String(a.mesafeMm) : ""}`;
    const tekAdaylar = [];
    for (const a of adaylar)
        if (!tekAdaylar.some((x) => anahtar(x) === anahtar(a)))
            tekAdaylar.push(a);
    const grup = (b) => (b === BOX_BAGLANTI ? 0 : b === PIN_BAGLANTI ? 2 : 1);
    tekAdaylar.sort((a, b) => {
        const ka = kesimKonumu(a, referans) ?? Number.POSITIVE_INFINITY;
        const kb = kesimKonumu(b, referans) ?? Number.POSITIVE_INFINITY;
        if (ka !== kb)
            return ka < kb ? -1 : 1;
        return grup(a.baglanti) - grup(b.baglanti) || (a.tur === b.tur ? 0 : a.tur === "kesim" ? -1 : 1);
    });
    // D-055: boydan boya değişimde yeni borunun ek kaynağı (en az iki boy), kesim ve kaynak tamiri satırlarından sonra.
    if (tamBoy)
        tekAdaylar.push({ tur: "ek_kaynagi", baglanti: BORU_BAGLANTI, mesafeMm: null });
    const kesimler = tekAdaylar.map((a, i) => ({
        sira: i + 1,
        tur: a.tur,
        mesafeMm: a.mesafeMm,
        baglanti: a.baglanti,
        durum: "onayli",
        iptalNedeni: null,
        not: null,
        kaynakNo: null,
        oneriMm: a.mesafeMm,
        oneriBaglanti: a.baglanti,
    }));
    // Şema bantları: bulgu başına haritanın Below Tolerance girdileri, yoksa okunan konumlar.
    const kusurBantlari = tekil(bulgular.filter((b) => b.tur === "boru").flatMap((b) => (b.haritaKonumlari.length ? b.haritaKonumlari : b.konumlar)));
    return {
        bulgular,
        kaynaklar,
        kusurBantlari,
        celiski: bulgular.some((b) => b.karar === "celiski"),
        tamBoy,
        referans,
        referansKaniti: kanit,
        kesimler,
        hidrolikUyarisi: g.hat === "Hydraulic" && (g.rotaKod === "MB" || g.rotaKod === "CKB"),
    };
}
/** tamir_kalemi.dispo_not için: bütün bulguların kapanış alıntıları " · " ile; hiç yoksa null. */
export function kapanisAlintilari(g) {
    const hepsi = [];
    for (const b of g.bulgular)
        for (const a of kapanisCumlesi(b.kaynakNot)?.alintilar ?? [])
            if (!hepsi.includes(a))
                hepsi.push(a);
    return hepsi.length ? hepsi.join(" · ") : null;
}
//# sourceMappingURL=kesim-onerisi.js.map